import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { serverTypeLabel } from '@/utils/server-type';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Plus, Edit, Trash2, X, Check, Filter, Trophy, Server, Settings,
  Tv, Tag, Layers, Power, Users, Download, Loader2, GripVertical,
  Play, Ban, Replace, Scissors, Type, ChevronDown, ChevronUp, HelpCircle, Globe, Upload, Zap, RefreshCw, Search,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  competitionsService, sportsService, xaccelService, teamsService, resolutionsService,
  eventFiltersService, channelsService, cdnService, scheduleService,
  Competition, Sport, XaccelServer, Team, Resolution, WizardTeam, getErrorMessage, getErrorCode, EpgEvent,
} from '@/services';
import { xaccelProxyService, XaccelProxy } from '@/services/xaccel-proxy.service';
import type { EventFilter as EventFilterType, FilterTestResult, CompetitionChannel, CdnName } from '@/services';
import { parseServerIds, parseProxyByResolution, getProxyForServer, PerServerProxyMap } from '@/utils/server-ids';
import CompetitionChannelsModal, { type CompetitionChannelsRequest } from '@/components/competitions/CompetitionChannelsModal';
import { compApiErrorText } from '@/utils/comp-channels';

// ============================================================================
// Types & Validation
// ============================================================================

const competitionSchema = z.object({
  sport_id: z.coerce.number().min(1, 'validation.required'),
  name: z.string().min(1, 'validation.required'),
  dazn_asset_id: z.string().optional(),
  xaccel_server_id: z.coerce.number().optional().nullable(),
  server_ids: z.array(z.number()),
  channel_prefix: z.string().optional(),
  cdn_blacklist: z.array(z.string()),
  cdn_preferred: z.array(z.string()),
  auto_stop_minutes: z.coerce.number().min(30).max(1440),
  stop_margin_minutes: z.coerce.number().min(0).max(120),
  num_channels: z.coerce.number().min(1).max(50),
  resolution_ids: z.array(z.number()),
  channel_mode: z.enum(['numbered', 'team', 'both']),
  is_active: z.boolean(),
  video_map_overrides: z.record(z.string()).optional(),
});

type CompetitionForm = z.infer<typeof competitionSchema>;

type ModalTab = 'general' | 'streaming' | 'teams' | 'filters' | 'channels' | 'special';

// ============================================================================
// Constants
// ============================================================================

const FILTER_TYPE_OPTIONS = [
  { value: 'exclude_keyword', label: 'filters.excludeKeyword', color: 'bg-error-600', icon: Ban },
  { value: 'exclude_regex', label: 'filters.excludeRegex', color: 'bg-error-600', icon: Ban },
  { value: 'replace_exact', label: 'filters.replaceExact', color: 'bg-primary-600', icon: Replace },
  { value: 'replace_regex', label: 'filters.replaceRegex', color: 'bg-primary-600', icon: Replace },
  { value: 'truncate', label: 'filters.truncate', color: 'bg-warning-600', icon: Scissors },
] as const;

const FILTER_HINTS: Record<string, string> = {
  exclude_keyword: 'filters.hintExcludeKeyword',
  exclude_regex: 'filters.hintExcludeRegex',
  replace_exact: 'filters.hintReplaceExact',
  replace_regex: 'filters.hintReplaceRegex',
  truncate: 'filters.hintTruncate',
};

const SHOW_REPLACEMENT = ['replace_exact', 'replace_regex'];

const DEFAULT_FORM_VALUES: CompetitionForm = {
  sport_id: 0,
  name: '',
  dazn_asset_id: '',
  xaccel_server_id: null,
  server_ids: [],
  channel_prefix: '',
  cdn_blacklist: [],
  cdn_preferred: [],
  auto_stop_minutes: 180,
  stop_margin_minutes: 0,
  num_channels: 10,
  resolution_ids: [],
  channel_mode: 'numbered',
  is_active: true,
  video_map_overrides: {},
};

// ============================================================================
// HintTooltip
// ============================================================================

function HintTooltip({ text }: { text: string }) {
  return (
    <span className="relative group/hint inline-flex ml-1 align-middle">
      <HelpCircle size={14} className="text-dark-500 hover:text-dark-300 cursor-help" />
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5
        bg-dark-100 text-white text-xs rounded-md shadow-lg
        opacity-0 invisible group-hover/hint:opacity-100 group-hover/hint:visible
        pointer-events-none transition-all duration-150 z-50 w-max max-w-[220px] text-center">
        {text}
      </span>
    </span>
  );
}

// ============================================================================
// Component
// ============================================================================

export default function Competitions() {
  const { t } = useTranslation();

  // Data state
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [xaccelServers, setXaccelServers] = useState<XaccelServer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);
  const [cdnNames, setCdnNames] = useState<CdnName[]>([]);
  const [cdnPreferredSearch, setCdnPreferredSearch] = useState('');
  const [cdnBlacklistSearch, setCdnBlacklistSearch] = useState('');
  const [xaccelProxies, setXaccelProxies] = useState<XaccelProxy[]>([]);
  const [proxyAssignments, setProxyAssignments] = useState<PerServerProxyMap>({});
  const [isSyncingProxies, setIsSyncingProxies] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null);
  // Create / delete competition channels: real state per server first, ticked servers only (phase 4).
  const [compChannelsReq, setCompChannelsReq] = useState<CompetitionChannelsRequest | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [filterSportId, setFilterSportId] = useState<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<ModalTab>('general');

  // Form
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CompetitionForm>({
    resolver: zodResolver(competitionSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchData = async () => {
    try {
      const [comps, sportsData, xaccelData, resData, cdnData, proxiesData] = await Promise.all([
        competitionsService.list({ sport_id: filterSportId }),
        sportsService.list(),
        xaccelService.list(),
        resolutionsService.list(),
        cdnService.list().catch(() => [] as CdnName[]),
        xaccelProxyService.listProxies().catch(() => [] as XaccelProxy[]),
      ]);
      setCompetitions(comps);
      setSports(sportsData);
      setXaccelServers(xaccelData);
      setResolutions(resData);
      setCdnNames(cdnData);
      setXaccelProxies(proxiesData);

      // Auto-sync proxies if empty and xaccel servers exist
      if (proxiesData.length === 0 && xaccelData.length > 0) {
        try {
          setIsSyncingProxies(true);
          const syncResult = await xaccelProxyService.syncProxies();
          if (syncResult.synced > 0) {
            const newProxies = await xaccelProxyService.listProxies();
            setXaccelProxies(newProxies);
          }
        } catch {
          // Ignore auto-sync errors
        } finally {
          setIsSyncingProxies(false);
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Manual sync proxies from streaming servers
  const handleSyncProxies = async () => {
    setIsSyncingProxies(true);
    try {
      await xaccelProxyService.syncProxies();
      const newProxies = await xaccelProxyService.listProxies();
      setXaccelProxies(newProxies);
      // Show count of proxies for the currently selected server
      const currentServerId = getValues('xaccel_server_id');
      const visibleCount = currentServerId
        ? newProxies.filter(p => p.xaccelServerId === Number(currentServerId)).length
        : newProxies.length;
      toast.success(t('proxies.synced', { count: visibleCount }));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsSyncingProxies(false);
    }
  };

  const fetchTeamsForCompetition = async (competitionId: number) => {
    try {
      const teamsData = await teamsService.list({ competition_id: competitionId });
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      setTeams([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterSportId]);

  // ============================================================================
  // Modal Handlers
  // ============================================================================

  const openCreateModal = () => {
    setEditingCompetition(null);
    setTeams([]);
    setActiveTab('general');
    setProxyAssignments({});
    setCdnPreferredSearch('');
    setCdnBlacklistSearch('');
    const defaultServer = xaccelServers.find(s => s.is_active === 1 && s.is_default === 1)
      || xaccelServers.find(s => s.is_active === 1);
    const hdResolution = resolutions.find(r => r.name.toUpperCase() === 'HD')
      || resolutions.find(r => r.name.toUpperCase().includes('HD'));
    const defaultServerIds = defaultServer ? [defaultServer.id] : [];
    reset({
      ...DEFAULT_FORM_VALUES,
      sport_id: sports[0]?.id || 0,
      xaccel_server_id: defaultServer?.id ?? null,
      server_ids: defaultServerIds,
      resolution_ids: hdResolution ? [hdResolution.id] : [],
    });
    setShowModal(true);
  };

  const openEditModal = async (competition: Competition) => {
    setEditingCompetition(competition);
    setActiveTab('general');
    setCdnPreferredSearch('');
    setCdnBlacklistSearch('');
    const editServerIds = parseServerIds(competition.server_ids);
    reset({
      sport_id: competition.sport_id,
      name: competition.name,
      dazn_asset_id: competition.dazn_asset_id || '',
      xaccel_server_id: competition.xaccel_server_id,
      server_ids: editServerIds.length > 0 ? editServerIds : (competition.xaccel_server_id ? [competition.xaccel_server_id] : []),
      channel_prefix: competition.channel_prefix || '',
      cdn_blacklist: competition.cdn_blacklist ? JSON.parse(competition.cdn_blacklist) : [],
      cdn_preferred: competition.cdn_preferred ? JSON.parse(competition.cdn_preferred) : [],
      auto_stop_minutes: competition.auto_stop_minutes,
      stop_margin_minutes: competition.stop_margin_minutes ?? 0,
      num_channels: competition.num_channels || 10,
      resolution_ids: competition.resolutions?.map(r => r.id) || [],
      channel_mode: competition.channel_mode || 'numbered',
      is_active: competition.is_active === 1,
      video_map_overrides: competition.video_map_overrides ? JSON.parse(competition.video_map_overrides) : {},
    });
    // Load proxy assignments (per-server format)
    const editSids = editServerIds.length > 0 ? editServerIds : (competition.xaccel_server_id ? [competition.xaccel_server_id] : []);
    setProxyAssignments(parseProxyByResolution(competition.proxy_by_resolution, editSids[0] ?? null));
    setShowModal(true);
    loadSpecialRules(competition);
    await Promise.all([
      fetchTeamsForCompetition(competition.id),
      fetchFiltersForCompetition(competition.id),
      fetchChannelsForCompetition(competition.id),
      fetchSpecialChannels(competition.id),
    ]);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCompetition(null);
    setTeams([]);
    setEventFilters([]);
    setChannels([]);
    setSpecialRules([]);
    setSpecialChannels([]);
    setProxyAssignments({});
    setDeleteChannelsConfirm(false);
    setDeleteSpecialConfirm(false);
    resetFilterForm();
    resetSpecialForm();
    setShowTestArea(false);
    setTestResult(null);
    setTestTitle('');
    // Reset EPG events
    setEpgEvents([]);
    setShowEpgEvents(false);
    setEpgMeta(null);
  };

  // ============================================================================
  // Form Submission
  // ============================================================================

  const onSubmit = async (data: CompetitionForm) => {
    try {
      const vmOverrides = data.video_map_overrides || {};
      const hasOverrides = Object.values(vmOverrides).some(v => v && v.trim() !== '');
      const payload = {
        sport_id: data.sport_id,
        name: data.name,
        dazn_asset_id: data.dazn_asset_id || undefined,
        xaccel_server_id: data.server_ids.length > 0 ? data.server_ids[0] : (data.xaccel_server_id || undefined),
        server_ids: data.server_ids.length > 0 ? data.server_ids : undefined,
        channel_prefix: data.channel_prefix || undefined,
        cdn_blacklist: JSON.stringify(data.cdn_blacklist || []),
        cdn_preferred: JSON.stringify(data.cdn_preferred || []),
        auto_stop_minutes: data.auto_stop_minutes,
        stop_margin_minutes: data.stop_margin_minutes,
        num_channels: data.num_channels,
        resolution_ids: data.resolution_ids,
        channel_mode: data.channel_mode,
        is_active: data.is_active,
        video_map_overrides: hasOverrides ? JSON.stringify(vmOverrides) : null,
      };

      if (editingCompetition) {
        await competitionsService.update(editingCompetition.id, payload);
        toast.success(t('common.updated'));
      } else {
        await competitionsService.create(payload);
        toast.success(t('common.created'));
      }

      closeModal();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  /**
   * Save form if there are unsaved changes (dirty state).
   * Called automatically before critical actions (provision, delete channels, etc.)
   * Returns true if save was successful or not needed, false on error.
   */
  const saveFormIfNeeded = async (): Promise<boolean> => {
    if (!editingCompetition || !isDirty) return true;

    try {
      const data = getValues();
      const vmOverrides = data.video_map_overrides || {};
      const hasOverrides = Object.values(vmOverrides).some(v => v && v.trim() !== '');

      await competitionsService.update(editingCompetition.id, {
        sport_id: data.sport_id,
        name: data.name,
        dazn_asset_id: data.dazn_asset_id || undefined,
        xaccel_server_id: data.server_ids.length > 0 ? data.server_ids[0] : (data.xaccel_server_id || undefined),
        server_ids: data.server_ids.length > 0 ? data.server_ids : undefined,
        channel_prefix: data.channel_prefix || undefined,
        cdn_blacklist: JSON.stringify(data.cdn_blacklist || []),
        cdn_preferred: JSON.stringify(data.cdn_preferred || []),
        auto_stop_minutes: data.auto_stop_minutes,
        stop_margin_minutes: data.stop_margin_minutes,
        num_channels: data.num_channels,
        resolution_ids: data.resolution_ids,
        channel_mode: data.channel_mode,
        is_active: data.is_active,
        video_map_overrides: hasOverrides ? JSON.stringify(vmOverrides) : null,
      });

      // Reset form to mark as not dirty (keep current values)
      reset(data);
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
      return false;
    }
  };

  const handleProxyAssign = async (serverId: number, resolution: string, proxyId: number | null) => {
    if (!editingCompetition) return;
    try {
      const result = await xaccelProxyService.assignToCompetition(editingCompetition.id, serverId, resolution, proxyId);
      if (result.success) {
        setProxyAssignments(prev => {
          const serverKey = String(serverId);
          return { ...prev, [serverKey]: { ...(prev[serverKey] || {}), [resolution]: proxyId } };
        });
        toast.success(t('proxies.saved'));
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const out = await competitionsService.delete(id);
      if (out.channelsLeft.length > 0) toast(t('competitions.deletedChannelsLeft', { count: out.channelsLeft.length, servers: [...new Set(out.channelsLeft.map((c) => c.server))].join(', ') }), { duration: 10000 });
      else toast.success(t('common.deleted'));
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDeleteAll = async () => {
    try {
      await competitionsService.deleteAll();
      toast.success(t('competitions.allDeleted'));
      setDeleteAllConfirm(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderResolutionBadges = (comp: Competition) => (
    <div className="flex flex-wrap gap-1">
      {comp.resolutions && comp.resolutions.length > 0
        ? comp.resolutions.map(r => (
            <span key={r.id} className="badge-primary text-xs">{r.name}</span>
          ))
        : <span className="text-dark-500 text-xs">—</span>
      }
    </div>
  );

  const renderStatusBadge = (comp: Competition) => (
    <span className={comp.is_active ? 'badge-success' : 'badge-error'}>
      {comp.is_active ? t('common.active') : t('common.inactive')}
    </span>
  );

  // ============================================================================
  // Modal Tabs
  // ============================================================================

  const renderGeneralTab = () => (
    <div className="space-y-6">
      {/* Info Section */}
      <div className="border-b border-dark-700 pb-4">
        <h3 className="text-sm font-medium text-dark-300 mb-4 flex items-center gap-2">
          <Trophy size={16} />
          {t('competitions.general')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">{t('sports.title')} *</label>
            <select
              className={errors.sport_id ? 'input-error' : 'input'}
              {...register('sport_id')}
            >
              <option value="">{t('competitions.selectSport')}</option>
              {sports.map((sport) => (
                <option key={sport.id} value={sport.id}>{sport.name}</option>
              ))}
            </select>
            {errors.sport_id && (
              <span className="text-xs text-error-600 mt-1">{t(errors.sport_id.message || '')}</span>
            )}
          </div>
          <div>
            <label className="label">{t('competitions.name')} *</label>
            <input
              type="text"
              className={errors.name ? 'input-error' : 'input'}
              placeholder="Serie A, Premier League..."
              {...register('name')}
            />
            {errors.name && (
              <span className="text-xs text-error-600 mt-1">{t(errors.name.message || '')}</span>
            )}
          </div>
        </div>
      </div>

      {/* DAZN Asset Section */}
      <div className="border-b border-dark-700 pb-4">
        <h3 className="text-sm font-medium text-dark-300 mb-4 flex items-center gap-2">
          <Tag size={16} />
          DAZN Asset
        </h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="label">Competition Asset ID</label>
            <input
              type="text"
              className="input bg-dark-800"
              placeholder="Es: 123456789abcdef"
              {...register('dazn_asset_id')}
            />
            <p className="text-xs text-dark-500 mt-1">
              {t('competitions.assetIdHelp')}
            </p>
          </div>
        </div>
      </div>

      {/* Status Section */}
      <div>
        <h3 className="text-sm font-medium text-dark-300 mb-4 flex items-center gap-2">
          <Power size={16} />
          {t('common.status')}
        </h3>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="w-5 h-5 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
            {...register('is_active')}
          />
          <div>
            <span className="text-sm text-dark-200 font-medium">{t('common.active')}</span>
            <p className="text-xs text-dark-500">
              {t('competitions.general')}
            </p>
          </div>
        </label>
      </div>
    </div>
  );

  const renderStreamingTab = () => (
    <div className="space-y-6">
      {/* Server Section */}
      <div className="border-b border-dark-700 pb-4">
        <h3 className="text-sm font-medium text-dark-300 mb-4 flex items-center gap-2">
          <Server size={16} />
          {t('competitions.streaming')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">{t('streaming.servers')} <HintTooltip text={t('linear.xaccelServerHint')} /></label>
            <Controller
              name="server_ids"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5 bg-dark-800 rounded-lg p-3 border border-dark-700">
                  {xaccelServers.filter(s => s.is_active === 1).length === 0 ? (
                    <p className="text-xs text-dark-500">{t('streaming.noServersSelected')}</p>
                  ) : (
                    xaccelServers.filter(s => s.is_active === 1).map((server) => {
                      const checked = (field.value || []).includes(server.id);
                      return (
                        <label key={server.id} className="flex items-center gap-2 cursor-pointer py-1 px-1 rounded hover:bg-dark-700 transition-colors">
                          <input
                            type="checkbox"
                            className="rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                            checked={checked}
                            onChange={() => {
                              const current = field.value || [];
                              const next = checked
                                ? current.filter((id: number) => id !== server.id)
                                : [...current, server.id];
                              field.onChange(next);
                              // Sync xaccel_server_id with first element
                              setValue('xaccel_server_id', next.length > 0 ? next[0] : null, { shouldDirty: true });
                            }}
                          />
                          <span className="text-sm">{server.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-dark-300">
                            {serverTypeLabel((server as any).server_type)}
                          </span>
                          {server.is_default === 1 && (
                            <span className="text-[10px] text-primary-400">(Default)</span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </div>

      {/* CDN Selection Section */}
      {cdnNames.length > 0 && (
        <div className="border-b border-dark-700 pb-4">
          <h3 className="text-sm font-medium text-dark-300 mb-4 flex items-center gap-2">
            <Globe size={16} />
            CDN Selection
            <HintTooltip text={t('linear.cdnPreferredHint')} />
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* CDN Preferred */}
            <div>
              <label className="label text-xs mb-2">{t('linear.cdnPreferred')}</label>
              <div className="relative mb-1.5">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-dark-500" />
                <input
                  type="text"
                  value={cdnPreferredSearch}
                  onChange={(e) => setCdnPreferredSearch(e.target.value)}
                  placeholder={t('common.search')}
                  className="input text-xs py-1 pl-7"
                />
              </div>
              <Controller
                name="cdn_preferred"
                control={control}
                render={({ field }) => (
                  <div className="space-y-1 max-h-32 overflow-y-auto border border-dark-700 rounded-md p-2">
                    {cdnNames
                      .filter(c => !c.is_blacklisted)
                      .filter(c => c.cdn_name.toLowerCase().includes(cdnPreferredSearch.trim().toLowerCase()))
                      .map((cdn) => {
                      const isSelected = (field.value || []).includes(cdn.cdn_name);
                      return (
                        <label
                          key={cdn.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-all ${
                            isSelected ? 'bg-primary-50 text-primary-600' : 'hover:bg-dark-800 text-dark-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const next = isSelected
                                ? (field.value || []).filter((n: string) => n !== cdn.cdn_name)
                                : [...(field.value || []), cdn.cdn_name];
                              field.onChange(next);
                            }}
                            className="w-3.5 h-3.5 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="font-mono">{cdn.cdn_name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              />
            </div>
            {/* CDN Blacklist */}
            <div>
              <label className="label text-xs mb-2">{t('linear.cdnBlacklist')}</label>
              <div className="relative mb-1.5">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-dark-500" />
                <input
                  type="text"
                  value={cdnBlacklistSearch}
                  onChange={(e) => setCdnBlacklistSearch(e.target.value)}
                  placeholder={t('common.search')}
                  className="input text-xs py-1 pl-7"
                />
              </div>
              <Controller
                name="cdn_blacklist"
                control={control}
                render={({ field }) => (
                  <div className="space-y-1 max-h-32 overflow-y-auto border border-dark-700 rounded-md p-2">
                    {cdnNames
                      .filter(c => c.cdn_name.toLowerCase().includes(cdnBlacklistSearch.trim().toLowerCase()))
                      .map((cdn) => {
                      const isSelected = (field.value || []).includes(cdn.cdn_name);
                      return (
                        <label
                          key={cdn.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-all ${
                            isSelected ? 'bg-error-50 text-error-600' : 'hover:bg-dark-800 text-dark-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const next = isSelected
                                ? (field.value || []).filter((n: string) => n !== cdn.cdn_name)
                                : [...(field.value || []), cdn.cdn_name];
                              field.onChange(next);
                            }}
                            className="w-3.5 h-3.5 rounded border-dark-600 text-error-600 focus:ring-error-500"
                          />
                          <span className="font-mono">{cdn.cdn_name}</span>
                          {cdn.is_blacklisted && (
                            <span className="text-error-500 text-[10px] ml-auto">Global BL</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      )}

      {/* Channel Section */}
      <div className="border-b border-dark-700 pb-4">
        <h3 className="text-sm font-medium text-dark-300 mb-4 flex items-center gap-2">
          <Tv size={16} />
          {t('competitions.channels')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Channel Name <HintTooltip text={t('competitions.channelPrefix')} /></label>
            <input
              type="text"
              className="input"
              placeholder="Es: SERIEA, PREMIERLEAGUE..."
              {...register('channel_prefix')}
            />
          </div>
          <div>
            <label className="label">{t('competitions.numChannels')} <HintTooltip text={t('competitions.numChannelsHint')} /></label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="input w-24"
                min="1"
                max="50"
                {...register('num_channels')}
              />
            </div>
          </div>
          <div>
            <label className="label">{t('competitions.autoStopHours')} <HintTooltip text={t('competitions.autoStopHoursHint')} /></label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="input w-24"
                min="30"
                max="1440"
                step="5"
                {...register('auto_stop_minutes')}
              />
              <span className="text-dark-400 text-sm">min</span>
            </div>
          </div>
          <div>
            <label className="label">{t('competitions.stopMarginMinutes')} <HintTooltip text={t('competitions.stopMarginMinutesHint')} /></label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="input w-24"
                min="0"
                max="120"
                step="5"
                {...register('stop_margin_minutes')}
              />
              <span className="text-dark-400 text-sm">min</span>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-sm font-medium text-dark-300 mb-3 flex items-center gap-2">
            <Tv size={16} />
            Channel Mode
            <HintTooltip text={t('competitions.channelNaming')} />
          </h3>
          <Controller
            name="channel_mode"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                {([
                  { value: 'numbered', labelKey: 'schedule.channelModeNumbered', example: 'CH_01_(FHD), CH_02_(FHD)' },
                  { value: 'team', labelKey: 'schedule.channelModeTeam', example: 'CH_Juventus_(FHD)' },
                  { value: 'both', labelKey: 'schedule.channelModeBoth', example: 'CH_01 + CH_Juventus_(FHD)' },
                ] as const).map((mode) => (
                  <label
                    key={mode.value}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-all ${
                      field.value === mode.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-dark-700 hover:border-dark-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="channel_mode"
                      checked={field.value === mode.value}
                      onChange={() => field.onChange(mode.value)}
                      className="w-4 h-4 text-primary-600 border-dark-600 focus:ring-primary-500"
                    />
                    <span className={`text-sm font-medium ${field.value === mode.value ? 'text-primary-600' : 'text-dark-200'}`}>
                      {t(mode.labelKey)}
                    </span>
                    <span className="text-xs text-dark-500 font-mono">{mode.example}</span>
                  </label>
                ))}
              </div>
            )}
          />
        </div>
      </div>

      {/* Resolution Section */}
      <div>
        <h3 className="text-sm font-medium text-dark-300 mb-4 flex items-center gap-2">
          <Layers size={16} />
          {t('competitions.resolutions')}
          <HintTooltip text={t('resolutions.title')} />
        </h3>
        {resolutions.length === 0 ? (
          <p className="text-dark-500 text-sm">{t('resolutions.noData')}</p>
        ) : (
          <Controller
            name="resolution_ids"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                {resolutions.map((res) => {
                  const isSelected = field.value.includes(res.id);
                  return (
                    <label
                      key={res.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-dark-700 hover:border-dark-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          const next = isSelected
                            ? field.value.filter((id: number) => id !== res.id)
                            : [...field.value, res.id];
                          field.onChange(next);
                        }}
                        className="w-4 h-4 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                      />
                      <span className={`text-sm font-medium ${isSelected ? 'text-primary-600' : 'text-dark-200'}`}>
                        {res.name}
                      </span>
                      <span className="text-xs text-dark-500 font-mono">{res.xaccel_value}</span>
                    </label>
                  );
                })}
              </div>
            )}
          />
        )}
      </div>

      {/* Video Map Override Section */}
      {(() => {
        const selectedResIds = watch('resolution_ids') || [];
        const selectedRes = resolutions.filter(r => selectedResIds.includes(r.id));
        const currentOverrides = watch('video_map_overrides') || {};
        if (selectedRes.length === 0) return null;
        return (
          <div>
            <h3 className="text-sm font-medium text-dark-300 mb-4 flex items-center gap-2">
              <Layers size={16} />
              Video Map Override
              <HintTooltip text={t('linear.videoMapOverrideHint')} />
            </h3>
            <div className="space-y-2">
              {selectedRes.map((res) => (
                <div key={res.id} className="flex items-center gap-3 px-3 py-2 rounded-md border border-dark-700">
                  <span className="text-sm font-medium text-dark-200 w-20">{res.name}</span>
                  <input
                    type="text"
                    className="input flex-1 text-sm font-mono"
                    value={currentOverrides[res.name] || ''}
                    onChange={(e) => {
                      const next = { ...currentOverrides, [res.name]: e.target.value };
                      setValue('video_map_overrides', next);
                    }}
                    placeholder={res.xaccel_value}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Proxy per Resolution Section - Per-server blocks */}
      {editingCompetition && xaccelServers.length > 0 && (() => {
        const selectedResIds = watch('resolution_ids') || [];
        const selectedRes = resolutions.filter(r => selectedResIds.includes(r.id));
        const selectedServerIds = watch('server_ids') || [];
        if (selectedServerIds.length === 0 || selectedRes.length === 0) return null;
        return (
          <div className="border-t border-dark-700 pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-dark-300 flex items-center gap-2">
                <Server size={16} />
                {t('proxies.proxyPerResolution')}
                <HintTooltip text={t('proxies.proxyPerResolutionHint')} />
              </h3>
              <button
                type="button"
                onClick={handleSyncProxies}
                disabled={isSyncingProxies}
                className="flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 disabled:opacity-50"
              >
                {isSyncingProxies ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RefreshCw size={14} />
                )}
                {t('proxies.sync')}
              </button>
            </div>
            <div className="space-y-4">
              {selectedServerIds.map((sId: number) => {
                const server = xaccelServers.find(s => s.id === sId);
                if (!server) return null;
                const sProxies = xaccelProxies.filter(p => p.xaccelServerId === sId);
                return (
                  <div key={sId} className="rounded-lg border border-dark-700 p-3">
                    <div className="text-xs font-medium text-dark-400 mb-2 flex items-center gap-2">
                      {server.name}
                      <span className="px-1.5 py-0.5 rounded bg-dark-600 text-dark-300">
                        {serverTypeLabel((server as any).server_type)}
                      </span>
                      {sProxies.length > 0 && <span className="text-dark-500">({sProxies.length})</span>}
                    </div>
                    {sProxies.length === 0 ? (
                      <div className="text-xs text-dark-500 text-center py-2">
                        {isSyncingProxies ? t('common.loading') : t('proxies.noProxiesSync')}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {selectedRes.map((res) => {
                          const currentProxyId = getProxyForServer(proxyAssignments, sId, res.name);
                          return (
                            <div key={res.id} className="flex items-center gap-3">
                              <span className="text-sm font-medium text-dark-200 w-20">{res.name}</span>
                              <select
                                className="input flex-1 text-sm"
                                value={currentProxyId || ''}
                                onChange={(e) => handleProxyAssign(sId, res.name, e.target.value ? Number(e.target.value) : null)}
                              >
                                <option value="">{t('proxies.noProxy')}</option>
                                {sProxies.map((proxy) => (
                                  <option key={proxy.id} value={proxy.id}>
                                    {proxy.name} ({proxy.host}:{proxy.port})
                                    {proxy.isOnline ? '' : ' - Offline'}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-dark-500 mt-2">{t('proxies.proxyAssignHelp')}</p>
          </div>
        );
      })()}
    </div>
  );

  // Teams tab state
  const [wizardTeams, setWizardTeams] = useState<WizardTeam[]>([]);
  const [wizardSelected, setWizardSelected] = useState<Set<string>>(new Set());
  const [showWizardPanel, setShowWizardPanel] = useState(false);
  const [isLoadingWizard, setIsLoadingWizard] = useState(false);
  const [isImportingTeams, setIsImportingTeams] = useState(false);
  const [showAddTeamForm, setShowAddTeamForm] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamShortName, setNewTeamShortName] = useState('');
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeamShortName, setEditTeamShortName] = useState('');
  const [deleteTeamConfirm, setDeleteTeamConfirm] = useState<number | null>(null);
  const [bulkDeleteAllConfirm, setBulkDeleteAllConfirm] = useState(false);

  const handleBulkDeleteAllTeams = async () => {
    if (!editingCompetition) return;
    try {
      const msg = await teamsService.deleteByCompetition(editingCompetition.id);
      toast.success(msg);
      setBulkDeleteAllConfirm(false);
      await fetchTeamsForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('teams.deleteError'));
    }
  };

  const handleOpenWizardPanel = async () => {
    if (!editingCompetition) return;
    setIsLoadingWizard(true);
    setShowWizardPanel(true);
    try {
      const data = await teamsService.getWizardData(editingCompetition.id);
      const newTeams = data.filter(t => !t.is_existing);
      setWizardTeams(newTeams);
      setWizardSelected(new Set(newTeams.map(t => t.dazn_asset_id!).filter(Boolean)));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('teams.fetchError'));
      setShowWizardPanel(false);
    } finally {
      setIsLoadingWizard(false);
    }
  };

  const handleImportSelectedTeams = async () => {
    if (!editingCompetition || wizardSelected.size === 0) return;
    setIsImportingTeams(true);
    try {
      const toImport = wizardTeams
        .filter(t => t.dazn_asset_id && wizardSelected.has(t.dazn_asset_id))
        .map(t => ({
          dazn_asset_id: t.dazn_asset_id,
          name: t.name,
          logo_url: t.logo_url,
        }));
      const result = await teamsService.importWizard(toImport, editingCompetition.id);
      toast.success(result.message || t('common.success'));
      setShowWizardPanel(false);
      setWizardTeams([]);
      setWizardSelected(new Set());
      await fetchTeamsForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('teams.importError'));
    } finally {
      setIsImportingTeams(false);
    }
  };

  const handleAddTeamManual = async () => {
    if (!editingCompetition || !newTeamName.trim()) return;
    try {
      await teamsService.create({
        competition_id: editingCompetition.id,
        name: newTeamName.trim(),
        short_name: newTeamShortName.trim() || undefined,
      });
      toast.success(t('competitions.teamAdded'));
      setNewTeamName('');
      setNewTeamShortName('');
      setShowAddTeamForm(false);
      await fetchTeamsForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('teams.importError'));
    }
  };

  const handleStartEditTeam = (team: Team) => {
    setEditingTeamId(team.id);
    setEditTeamName(team.name);
    setEditTeamShortName(team.short_name || '');
  };

  const handleSaveEditTeam = async () => {
    if (!editingTeamId || !editTeamName.trim()) return;
    try {
      await teamsService.update(editingTeamId, {
        name: editTeamName.trim(),
        short_name: editTeamShortName.trim() || null,
      });
      toast.success(t('competitions.teamUpdated'));
      setEditingTeamId(null);
      if (editingCompetition) await fetchTeamsForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    try {
      await teamsService.delete(teamId);
      toast.success(t('competitions.teamDeleted'));
      setDeleteTeamConfirm(null);
      if (editingCompetition) await fetchTeamsForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('teams.deleteError'));
    }
  };

  const renderTeamsTab = () => (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-dark-300 flex items-center gap-2">
          <Users size={16} />
          {t('competitions.teams')} ({teams.length})
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleOpenWizardPanel}
            disabled={!editingCompetition?.dazn_asset_id || isLoadingWizard}
            className="btn-secondary text-xs py-1.5 px-3"
            title={!editingCompetition?.dazn_asset_id ? '' : ''}
          >
            {isLoadingWizard ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            {t('competitions.importFromDazn')}
          </button>
          <button
            type="button"
            onClick={() => setShowAddTeamForm(true)}
            className="btn-secondary text-xs py-1.5 px-3"
          >
            <Plus size={14} />
            {t('competitions.addManual')}
          </button>
          {teams.length > 0 && (
            bulkDeleteAllConfirm ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleBulkDeleteAllTeams}
                  className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                  title={t('common.confirmDelete')}
                >
                  <Check size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setBulkDeleteAllConfirm(false)}
                  className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
                  title={t('common.cancel')}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setBulkDeleteAllConfirm(true)}
                className="btn-secondary text-xs py-1.5 px-3 text-error-600 hover:text-error-700 hover:border-error-200"
                title={t('common.deleteAll')}
              >
                <Trash2 size={14} />
                {t('common.deleteAll')}
              </button>
            )
          )}
        </div>
      </div>

      {/* Wizard Panel */}
      {showWizardPanel && (
        <div className="border border-primary-200 rounded-lg bg-primary-50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-primary-600">
              {t('competitions.importFromDazn')} ({wizardTeams.length})
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setWizardSelected(new Set(wizardTeams.map(t => t.dazn_asset_id!).filter(Boolean)))}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                {t('common.selectAll')}
              </button>
              <button
                type="button"
                onClick={() => setWizardSelected(new Set())}
                className="text-xs text-dark-400 hover:text-dark-300"
              >
                {t('common.deselectAll')}
              </button>
            </div>
          </div>

          {isLoadingWizard ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={24} className="animate-spin text-primary-600" />
              <span className="ml-2 text-dark-400 text-sm">{t('competitions.loadingTeams')}</span>
            </div>
          ) : wizardTeams.length === 0 ? (
            <p className="text-dark-400 text-sm text-center py-4">
              {t('competitions.allTeamsImported')}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {wizardTeams.map((wt) => {
                const key = wt.dazn_asset_id || wt.name;
                const isChecked = wt.dazn_asset_id ? wizardSelected.has(wt.dazn_asset_id) : false;
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                      isChecked
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-dark-700 hover:border-dark-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {
                        if (!wt.dazn_asset_id) return;
                        const next = new Set(wizardSelected);
                        if (next.has(wt.dazn_asset_id)) {
                          next.delete(wt.dazn_asset_id);
                        } else {
                          next.add(wt.dazn_asset_id);
                        }
                        setWizardSelected(next);
                      }}
                      className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                    />
                    {wt.logo_url && (
                      <img src={wt.logo_url} alt="" className="w-5 h-5 rounded object-contain" />
                    )}
                    <span className="truncate text-dark-200">{wt.name}</span>
                  </label>
                );
              })}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-dark-700">
            <button
              type="button"
              onClick={() => { setShowWizardPanel(false); setWizardTeams([]); setWizardSelected(new Set()); }}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleImportSelectedTeams}
              disabled={wizardSelected.size === 0 || isImportingTeams}
              className="btn-primary text-xs py-1.5 px-3"
            >
              {isImportingTeams ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {t('sports.importSelected')} ({wizardSelected.size})
            </button>
          </div>
        </div>
      )}

      {/* Add Manual Team Form */}
      {showAddTeamForm && (
        <div className="border border-dark-600 rounded-lg bg-dark-800 p-4 space-y-3">
          <h4 className="text-sm font-medium text-dark-300">{t('competitions.newTeam')}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-xs">{t('common.name')} *</label>
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Juventus"
                className="input text-sm"
              />
            </div>
            <div>
              <label className="label text-xs">Short Name</label>
              <input
                type="text"
                value={newTeamShortName}
                onChange={(e) => setNewTeamShortName(e.target.value)}
                placeholder="JUV"
                className="input text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowAddTeamForm(false); setNewTeamName(''); setNewTeamShortName(''); }}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAddTeamManual}
              disabled={!newTeamName.trim()}
              className="btn-primary text-xs py-1.5 px-3"
            >
              {t('common.add')}
            </button>
          </div>
        </div>
      )}

      {/* Team List */}
      {teams.length === 0 ? (
        <div className="text-center py-8 text-dark-400">
          <Users size={48} className="mx-auto mb-3 opacity-50" />
          <p>{t('common.noData')}</p>
          <p className="text-sm mt-1">
            {t('competitions.addTeamsHelp')}
          </p>
        </div>
      ) : (
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {teams.map((team) => (
            <div
              key={team.id}
              className="flex items-center gap-3 p-2.5 rounded-lg bg-dark-800 border border-dark-700"
            >
              {team.logo_url ? (
                <img
                  src={team.logo_url}
                  alt={team.name}
                  className="w-8 h-8 rounded object-contain bg-dark-800"
                />
              ) : (
                <div className="w-8 h-8 rounded bg-dark-800 flex items-center justify-center">
                  <Users size={16} className="text-dark-500" />
                </div>
              )}

              {editingTeamId === team.id ? (
                /* Inline Edit Mode */
                <div className="flex-1 flex items-center gap-2">
                  <input
                    type="text"
                    value={editTeamName}
                    onChange={(e) => setEditTeamName(e.target.value)}
                    className="input text-sm py-1 flex-1"
                    placeholder="Nome"
                  />
                  <input
                    type="text"
                    value={editTeamShortName}
                    onChange={(e) => setEditTeamShortName(e.target.value)}
                    className="input text-sm py-1 w-24"
                    placeholder="Short"
                  />
                  <button
                    type="button"
                    onClick={handleSaveEditTeam}
                    className="p-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-500"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingTeamId(null)}
                    className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                /* Display Mode */
                <>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-dark-200 truncate">{team.name}</p>
                    {team.short_name && (
                      <p className="text-xs text-dark-500">{team.short_name}</p>
                    )}
                  </div>
                  {deleteTeamConfirm === team.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleDeleteTeam(team.id)}
                        className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTeamConfirm(null)}
                        className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleStartEditTeam(team)}
                        className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400 hover:text-dark-100"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTeamConfirm(team.id)}
                        className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ============================================================================
  // Filters Tab
  // ============================================================================

  const [eventFilters, setEventFilters] = useState<EventFilterType[]>([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [showFilterForm, setShowFilterForm] = useState(false);
  const [editingFilter, setEditingFilter] = useState<EventFilterType | null>(null);
  const [filterFormName, setFilterFormName] = useState('');
  const [filterFormType, setFilterFormType] = useState<EventFilterType['filter_type']>('exclude_keyword');

  // EPG Events for filters
  const [epgEvents, setEpgEvents] = useState<EpgEvent[]>([]);
  const [isLoadingEpgEvents, setIsLoadingEpgEvents] = useState(false);
  const [showEpgEvents, setShowEpgEvents] = useState(false);
  const [epgMeta, setEpgMeta] = useState<{ timezone: string; total_events: number } | null>(null);
  const [filterFormPattern, setFilterFormPattern] = useState('');
  const [filterFormReplacement, setFilterFormReplacement] = useState('');
  const [deleteFilterConfirm, setDeleteFilterConfirm] = useState<number | null>(null);
  const [showTestArea, setShowTestArea] = useState(false);
  const [testTitle, setTestTitle] = useState('');
  const [testResult, setTestResult] = useState<FilterTestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const dndSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchFiltersForCompetition = async (competitionId: number) => {
    setIsLoadingFilters(true);
    try {
      const data = await eventFiltersService.list(competitionId);
      setEventFilters(data);
    } catch (error) {
      console.error('Failed to fetch event filters:', error);
      setEventFilters([]);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const fetchEpgEvents = async () => {
    if (!editingCompetition) return;
    setIsLoadingEpgEvents(true);
    try {
      const result = await competitionsService.getEpgEvents(editingCompetition.id);
      setEpgEvents(result.events);
      setEpgMeta({ timezone: result.meta.timezone, total_events: result.meta.total_events });
      setShowEpgEvents(true);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('filters.epgLoadError'));
      setEpgEvents([]);
    } finally {
      setIsLoadingEpgEvents(false);
    }
  };

  const handleSelectEpgEvent = (event: EpgEvent) => {
    // Open filter form with event title as pattern
    setFilterFormPattern(event.title);
    setFilterFormName(event.title.substring(0, 30));
    setShowFilterForm(true);
    setEditingFilter(null);
  };

  const handleCreateFilter = async () => {
    if (!editingCompetition || !filterFormName.trim() || !filterFormPattern.trim()) return;
    try {
      await eventFiltersService.create({
        competition_id: editingCompetition.id,
        name: filterFormName.trim(),
        filter_type: filterFormType,
        pattern: filterFormPattern.trim(),
        replacement: filterFormReplacement,
      });
      toast.success(t('filters.created'));
      resetFilterForm();
      await fetchFiltersForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('filters.createError'));
    }
  };

  const handleUpdateFilter = async () => {
    if (!editingFilter || !filterFormName.trim() || !filterFormPattern.trim()) return;
    try {
      await eventFiltersService.update(editingFilter.id, {
        name: filterFormName.trim(),
        filter_type: filterFormType,
        pattern: filterFormPattern.trim(),
        replacement: filterFormReplacement,
      });
      toast.success(t('filters.updated'));
      resetFilterForm();
      if (editingCompetition) await fetchFiltersForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('filters.updated'));
    }
  };

  const handleToggleFilter = async (id: number) => {
    try {
      await eventFiltersService.toggle(id);
      if (editingCompetition) await fetchFiltersForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDeleteFilter = async (id: number) => {
    try {
      await eventFiltersService.delete(id);
      toast.success(t('filters.deleted'));
      setDeleteFilterConfirm(null);
      if (editingCompetition) await fetchFiltersForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleFilterDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = eventFilters.findIndex(f => f.id === active.id);
    const newIndex = eventFilters.findIndex(f => f.id === over.id);
    const reordered = arrayMove(eventFilters, oldIndex, newIndex);
    setEventFilters(reordered);

    try {
      await eventFiltersService.reorder(reordered.map(f => f.id));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('filters.reorderError'));
      if (editingCompetition) await fetchFiltersForCompetition(editingCompetition.id);
    }
  };

  const handleTestFilters = async () => {
    if (!editingCompetition || !testTitle.trim()) return;
    setIsTesting(true);
    try {
      const result = await eventFiltersService.test(editingCompetition.id, testTitle.trim());
      setTestResult(result);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('filters.testError'));
    } finally {
      setIsTesting(false);
    }
  };

  const startEditFilter = (filter: EventFilterType) => {
    setEditingFilter(filter);
    setFilterFormName(filter.name);
    setFilterFormType(filter.filter_type);
    setFilterFormPattern(filter.pattern);
    setFilterFormReplacement(filter.replacement);
    setShowFilterForm(true);
  };

  const resetFilterForm = () => {
    setShowFilterForm(false);
    setEditingFilter(null);
    setFilterFormName('');
    setFilterFormType('exclude_keyword');
    setFilterFormPattern('');
    setFilterFormReplacement('');
  };

  const getFilterTypeBadge = (filterType: string) => {
    const opt = FILTER_TYPE_OPTIONS.find(o => o.value === filterType);
    if (!opt) return null;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-white ${opt.color}`}>
        {t(opt.label)}
      </span>
    );
  };

  const SortableFilterItem = ({ filter }: { filter: EventFilterType }) => {
    const {
      attributes, listeners, setNodeRef, transform, transition, isDragging,
    } = useSortable({ id: filter.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center gap-3 p-2.5 rounded-lg border ${
          filter.is_enabled ? 'bg-dark-800 border-dark-700' : 'bg-dark-800 border-dark-800 opacity-60'
        }`}
      >
        <button
          type="button"
          className="cursor-grab text-dark-500 hover:text-dark-300"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>

        <button
          type="button"
          onClick={() => handleToggleFilter(filter.id)}
          className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${
            filter.is_enabled ? 'bg-primary-600' : 'bg-dark-600'
          }`}
        >
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            filter.is_enabled ? 'left-4' : 'left-0.5'
          }`} />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-dark-200 truncate">{filter.name}</span>
            {getFilterTypeBadge(filter.filter_type)}
          </div>
          <p className="text-xs text-dark-500 font-mono truncate mt-0.5">
            {filter.pattern}
            {filter.replacement && <span className="text-dark-400"> → {filter.replacement}</span>}
          </p>
        </div>

        {deleteFilterConfirm === filter.id ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleDeleteFilter(filter.id)}
              className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
            >
              <Check size={14} />
            </button>
            <button
              type="button"
              onClick={() => setDeleteFilterConfirm(null)}
              className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => startEditFilter(filter)}
              className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400 hover:text-dark-100"
            >
              <Edit size={14} />
            </button>
            <button
              type="button"
              onClick={() => setDeleteFilterConfirm(filter.id)}
              className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderFiltersTab = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-dark-300 flex items-center gap-2">
          <Filter size={16} />
          {t('filters.title')} ({eventFilters.length})
        </h3>
        <button
          type="button"
          onClick={() => { resetFilterForm(); setShowFilterForm(true); }}
          className="btn-secondary text-xs py-1.5 px-3"
        >
          <Plus size={14} />
          {t('filters.add')}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showFilterForm && (
        <div className="border border-dark-600 rounded-lg bg-dark-800 p-4 space-y-3">
          <h4 className="text-sm font-medium text-dark-300">
            {editingFilter ? t('common.edit') : t('filters.add')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label text-xs">{t('common.name')} *</label>
              <input
                type="text"
                value={filterFormName}
                onChange={(e) => setFilterFormName(e.target.value)}
                placeholder={t('filters.name')}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="label text-xs">{t('filters.type')} *</label>
              <select
                value={filterFormType}
                onChange={(e) => setFilterFormType(e.target.value as EventFilterType['filter_type'])}
                className="input text-sm"
              >
                {FILTER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* EPG Events Selector */}
          <div className="border border-dark-600 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => showEpgEvents ? setShowEpgEvents(false) : fetchEpgEvents()}
              className="w-full flex items-center justify-between px-3 py-2 text-xs text-dark-400 hover:bg-dark-700/50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Tv size={14} className="text-primary-500" />
                {t('filters.epgEvents')}
                {epgMeta && <span className="text-dark-500">({epgMeta.total_events})</span>}
              </span>
              {isLoadingEpgEvents ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                showEpgEvents ? <ChevronUp size={14} /> : <ChevronDown size={14} />
              )}
            </button>
            {showEpgEvents && (
              <div className="border-t border-dark-600 max-h-32 overflow-y-auto">
                {epgEvents.length === 0 ? (
                  <div className="px-3 py-4 text-center text-dark-500 text-xs">
                    {t('filters.noEvents')}
                  </div>
                ) : (
                  <div className="divide-y divide-dark-700">
                    {epgEvents.map((event, idx) => (
                      <button
                        key={`${event.asset_id}-${idx}`}
                        type="button"
                        onClick={() => handleSelectEpgEvent(event)}
                        className="w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-dark-700/50 transition-colors group"
                      >
                        <span className="text-[10px] font-mono text-primary-400 whitespace-nowrap">
                          {event.date_formatted} {event.start_time_local}
                        </span>
                        <span className="text-xs text-dark-300 truncate flex-1">
                          {event.title}
                        </span>
                        <Plus size={12} className="text-dark-500 opacity-0 group-hover:opacity-100 shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="label text-xs">{t('filters.pattern')} *</label>
            <input
              type="text"
              value={filterFormPattern}
              onChange={(e) => setFilterFormPattern(e.target.value)}
              placeholder={filterFormType === 'truncate' ? 'Es: .' : 'Es: Studio Tile'}
              className="input text-sm font-mono"
            />
            <p className="text-xs text-dark-500 mt-1">
              {t(FILTER_HINTS[filterFormType])}
            </p>
          </div>
          {SHOW_REPLACEMENT.includes(filterFormType) && (
            <div>
              <label className="label text-xs">{t('filters.replacement')}</label>
              <input
                type="text"
                value={filterFormReplacement}
                onChange={(e) => setFilterFormReplacement(e.target.value)}
                placeholder={t('filters.replacement')}
                className="input text-sm font-mono"
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetFilterForm}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={editingFilter ? handleUpdateFilter : handleCreateFilter}
              disabled={!filterFormName.trim() || !filterFormPattern.trim()}
              className="btn-primary text-xs py-1.5 px-3"
            >
              {editingFilter ? t('common.update') : t('common.create')}
            </button>
          </div>
        </div>
      )}

      {/* Filter List with DnD */}
      {isLoadingFilters ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin text-primary-600" />
          <span className="ml-2 text-dark-400 text-sm">{t('filters.loading')}</span>
        </div>
      ) : eventFilters.length === 0 ? (
        <div className="text-center py-8 text-dark-400">
          <Filter size={48} className="mx-auto mb-3 opacity-50" />
          <p>{t('filters.noFilters')}</p>
          <p className="text-sm mt-1">
            {t('filters.title')}
          </p>
        </div>
      ) : (
        <DndContext
          sensors={dndSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleFilterDragEnd}
        >
          <SortableContext
            items={eventFilters.map(f => f.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-1">
              {eventFilters.map((filter) => (
                <SortableFilterItem key={filter.id} filter={filter} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Test Area */}
      <div className="border-t border-dark-700 pt-4">
        <button
          type="button"
          onClick={() => setShowTestArea(!showTestArea)}
          className="flex items-center gap-2 text-sm text-dark-400 hover:text-dark-200"
        >
          {showTestArea ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          <Play size={14} />
          {t('filters.test')}
        </button>

        {showTestArea && (
          <div className="mt-3 space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={testTitle}
                onChange={(e) => setTestTitle(e.target.value)}
                placeholder={t('filters.testTitle')}
                className="input text-sm flex-1 font-mono"
              />
              <button
                type="button"
                onClick={handleTestFilters}
                disabled={!testTitle.trim() || isTesting}
                className="btn-primary text-xs py-1.5 px-4"
              >
                {isTesting ? <Loader2 size={14} className="animate-spin" /> : t('filters.test')}
              </button>
            </div>

            {testResult && (
              <div className={`p-3 rounded-lg border ${
                testResult.excluded
                  ? 'border-error-200 bg-error-50'
                  : testResult.transformedTitle !== testResult.originalTitle
                    ? 'border-primary-200 bg-primary-50'
                    : 'border-dark-700 bg-dark-800'
              }`}>
                {testResult.excluded ? (
                  <div className="flex items-center gap-2">
                    <Ban size={16} className="text-error-600" />
                    <span className="text-sm font-medium text-error-600">{t('filters.excluded')}</span>
                  </div>
                ) : testResult.transformedTitle !== testResult.originalTitle ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Type size={14} className="text-primary-600" />
                      <span className="text-xs text-dark-400">{t('filters.testTitle')}:</span>
                      <span className="text-sm text-dark-300 font-mono line-through">{testResult.originalTitle}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Replace size={14} className="text-primary-600" />
                      <span className="text-xs text-dark-400">{t('filters.testResult')}:</span>
                      <span className="text-sm text-primary-700 font-mono font-medium">{testResult.transformedTitle}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-dark-400">{t('filters.noFilters')}</p>
                )}

                {testResult.appliedFilters.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-dark-700">
                    <span className="text-xs text-dark-500">
                      {testResult.appliedFilters.length} {t('filters.filterApplied')}:
                      {' '}{testResult.appliedFilters.map(f => f.name).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================================================
  // Special Channels Tab
  // ============================================================================

  interface SpecialChannelRule {
    name: string;
    match_patterns: string[];
    match_type: 'contains' | 'exact';
    channel_name?: string;
    channel_suffix?: string; // backward compat
    num_channels: number;
    resolution_ids?: number[];
    xaccel_user_agent?: string;
    video_map_overrides?: Record<string, string>;
  }

  const [specialRules, setSpecialRules] = useState<SpecialChannelRule[]>([]);
  const [showSpecialForm, setShowSpecialForm] = useState(false);
  const [editingSpecialIdx, setEditingSpecialIdx] = useState<number | null>(null);
  const [specialFormName, setSpecialFormName] = useState('');
  const [specialFormPatterns, setSpecialFormPatterns] = useState('');
  const [specialFormMatchType, setSpecialFormMatchType] = useState<'contains' | 'exact'>('contains');
  const [specialFormChannelName, setSpecialFormChannelName] = useState('');
  const [specialFormNumChannels, setSpecialFormNumChannels] = useState(1);
  const [specialFormResolutionIds, setSpecialFormResolutionIds] = useState<number[]>([]);
  const [specialFormUserAgent, setSpecialFormUserAgent] = useState('');
  const [specialFormVideoMapOverrides, setSpecialFormVideoMapOverrides] = useState<Record<string, string>>({});
  const [isSavingSpecial, setIsSavingSpecial] = useState(false);
  const [specialChannels, setSpecialChannels] = useState<CompetitionChannel[]>([]);
  const [isLoadingSpecialChannels, setIsLoadingSpecialChannels] = useState(false);
  // Busy while the create / delete preview of the special channels is open (the modal runs the action).
  const isProvisioningSpecial = compChannelsReq?.action === 'create' && compChannelsReq.scope === 'special';
  const isDeletingSpecial = compChannelsReq?.action === 'delete' && compChannelsReq.scope === 'special';
  const [deleteSpecialConfirm, setDeleteSpecialConfirm] = useState(false);
  const [deleteSpecialDbConfirm, setDeleteSpecialDbConfirm] = useState(false);
  const [isDeletingSpecialDb, setIsDeletingSpecialDb] = useState(false);

  const loadSpecialRules = (competition: Competition) => {
    if (competition.special_events_mapping) {
      try {
        setSpecialRules(JSON.parse(competition.special_events_mapping));
      } catch {
        setSpecialRules([]);
      }
    } else {
      setSpecialRules([]);
    }
  };

  const resetSpecialForm = () => {
    setShowSpecialForm(false);
    setEditingSpecialIdx(null);
    setSpecialFormName('');
    setSpecialFormPatterns('');
    setSpecialFormMatchType('contains');
    setSpecialFormChannelName('');
    setSpecialFormNumChannels(1);
    setSpecialFormResolutionIds([]);
    setSpecialFormUserAgent('');
    setSpecialFormVideoMapOverrides({});
  };

  const openEditSpecialRule = (idx: number) => {
    const rule = specialRules[idx];
    setEditingSpecialIdx(idx);
    setSpecialFormName(rule.name);
    setSpecialFormPatterns(rule.match_patterns.join('\n'));
    setSpecialFormMatchType(rule.match_type);
    setSpecialFormChannelName(rule.channel_name || rule.channel_suffix || '');
    setSpecialFormNumChannels(rule.num_channels);
    setSpecialFormResolutionIds(rule.resolution_ids || []);
    setSpecialFormUserAgent(rule.xaccel_user_agent || '');
    setSpecialFormVideoMapOverrides(rule.video_map_overrides || {});
    setShowSpecialForm(true);
  };

  const fetchSpecialChannels = async (competitionId: number) => {
    setIsLoadingSpecialChannels(true);
    try {
      const data = await channelsService.listByType(competitionId, 'special');
      setSpecialChannels(data);
    } catch {
      setSpecialChannels([]);
    } finally {
      setIsLoadingSpecialChannels(false);
    }
  };

  const handleProvisionSpecialChannels = async () => {
    if (!editingCompetition) return;
    // Auto-save form before the preview so the plan uses the latest rules / servers
    const saved = await saveFormIfNeeded();
    if (!saved) return;
    setCompChannelsReq({ action: 'create', scope: 'special', serverIds: watch('server_ids') || [] });
  };

  const handleDeleteSpecialChannels = async () => {
    if (!editingCompetition) return;
    const saved = await saveFormIfNeeded();
    if (!saved) return;
    setDeleteSpecialConfirm(false);
    setCompChannelsReq({ action: 'delete', scope: 'special', serverIds: [...new Set(specialChannels.map((c) => c.xaccel_server_id))], legIds: specialChannels.map((c) => c.id) });
  };

  const handleDeleteSpecialChannelsDb = async () => {
    if (!editingCompetition) return;
    setIsDeletingSpecialDb(true);
    try {
      await channelsService.deleteSpecialDbOnly(editingCompetition.id);
      toast.success(t('common.deleted'));
      setSpecialChannels([]);
      setDeleteSpecialDbConfirm(false);
      fetchChannelsForCompetition(editingCompetition.id);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsDeletingSpecialDb(false);
    }
  };

  const handleDeleteSpecialChannel = async (channelId: number) => {
    if (!editingCompetition) return;
    try {
      await channelsService.deleteSingle(editingCompetition.id, channelId);
      setSpecialChannels(prev => prev.filter(c => c.id !== channelId));
      setChannels(prev => prev.filter(c => c.id !== channelId));
      setSpecialChannelDeleteConfirmId(null);
      toast.success(t('common.deleted'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDeleteSpecialChannelDb = async (channelId: number) => {
    if (!editingCompetition) return;
    try {
      await channelsService.deleteSingleDbOnly(editingCompetition.id, channelId);
      setSpecialChannels(prev => prev.filter(c => c.id !== channelId));
      setChannels(prev => prev.filter(c => c.id !== channelId));
      setSpecialChannelDeleteConfirmId(null);
      toast.success(t('common.deleted'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const saveSpecialRules = async (updatedRules: SpecialChannelRule[]) => {
    if (!editingCompetition) return;
    setIsSavingSpecial(true);
    try {
      await competitionsService.update(editingCompetition.id, {
        special_events_mapping: updatedRules.length > 0 ? JSON.stringify(updatedRules) : null,
      });
      setSpecialRules(updatedRules);
      toast.success(t('specialChannels.saved'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('specialChannels.saveError'));
    } finally {
      setIsSavingSpecial(false);
    }
  };

  const handleAddOrUpdateSpecialRule = async () => {
    const patterns = specialFormPatterns.split('\n').map(p => p.trim()).filter(p => p.length > 0);
    if (!specialFormName.trim() || patterns.length === 0 || !specialFormChannelName.trim()) return;

    // Clean video_map_overrides: remove empty values
    const cleanedOverrides: Record<string, string> = {};
    for (const [key, val] of Object.entries(specialFormVideoMapOverrides)) {
      if (val.trim()) cleanedOverrides[key] = val.trim();
    }

    const rule: SpecialChannelRule = {
      name: specialFormName.trim(),
      match_patterns: patterns,
      match_type: specialFormMatchType,
      channel_name: specialFormChannelName.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, ''),
      num_channels: specialFormNumChannels,
      resolution_ids: specialFormResolutionIds.length > 0 ? specialFormResolutionIds : undefined,
      xaccel_user_agent: specialFormUserAgent.trim() || undefined,
      video_map_overrides: Object.keys(cleanedOverrides).length > 0 ? cleanedOverrides : undefined,
    };

    let updated: SpecialChannelRule[];
    if (editingSpecialIdx !== null) {
      updated = [...specialRules];
      updated[editingSpecialIdx] = rule;
    } else {
      updated = [...specialRules, rule];
    }

    await saveSpecialRules(updated);
    resetSpecialForm();
  };

  const handleDeleteSpecialRule = async (idx: number) => {
    const updated = specialRules.filter((_, i) => i !== idx);
    await saveSpecialRules(updated);
  };

  const renderSpecialChannelsTab = () => {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-dark-300 flex items-center gap-2">
              <Zap size={16} />
              {t('specialChannels.title')} ({specialRules.length})
            </h3>
            <p className="text-xs text-dark-500 mt-1">{t('specialChannels.description')}</p>
          </div>
          <button
            type="button"
            onClick={() => { resetSpecialForm(); setShowSpecialForm(true); }}
            className="btn-secondary text-xs py-1.5 px-3"
          >
            <Plus size={14} />
            {t('specialChannels.addRule')}
          </button>
        </div>

        {/* Add/Edit Form */}
        {showSpecialForm && (
          <div className="border border-dark-600 rounded-lg bg-dark-800 p-4 space-y-3">
            <h4 className="text-sm font-medium text-dark-300">
              {editingSpecialIdx !== null ? t('specialChannels.editRule') : t('specialChannels.addRule')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label text-xs">{t('specialChannels.name')} *</label>
                <input
                  type="text"
                  value={specialFormName}
                  onChange={(e) => setSpecialFormName(e.target.value)}
                  placeholder={t('specialChannels.namePlaceholder')}
                  className="input text-sm"
                />
              </div>
              <div>
                <label className="label text-xs">{t('specialChannels.matchType')}</label>
                <select
                  value={specialFormMatchType}
                  onChange={(e) => setSpecialFormMatchType(e.target.value as 'contains' | 'exact')}
                  className="input text-sm"
                >
                  <option value="contains">{t('specialChannels.matchTypeContains')}</option>
                  <option value="exact">{t('specialChannels.matchTypeExact')}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label text-xs">{t('specialChannels.matchPatterns')} *</label>
              <textarea
                value={specialFormPatterns}
                onChange={(e) => setSpecialFormPatterns(e.target.value)}
                placeholder="Zona DAZN&#10;DAZN Zone"
                rows={3}
                className="input text-sm font-mono"
              />
              <p className="text-xs text-dark-500 mt-1">{t('specialChannels.matchPatternsHelp')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label text-xs">{t('specialChannels.channelName')} *</label>
                <input
                  type="text"
                  value={specialFormChannelName}
                  onChange={(e) => setSpecialFormChannelName(e.target.value)}
                  placeholder="SERIE_A_ZONA_DAZN"
                  className="input text-sm font-mono uppercase"
                />
                <p className="text-xs text-dark-500 mt-1">{t('specialChannels.channelNameHelp')}</p>
              </div>
              <div>
                <label className="label text-xs">{t('specialChannels.numChannels')}</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={specialFormNumChannels}
                  onChange={(e) => setSpecialFormNumChannels(Number(e.target.value))}
                  className="input text-sm"
                />
                <p className="text-xs text-dark-500 mt-1">{t('specialChannels.numChannelsHelp')}</p>
              </div>
            </div>
            {/* Resolutions selection */}
            <div>
              <label className="label text-xs">{t('specialChannels.resolutions')}</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {resolutions.map((res) => (
                  <label key={res.id} className="flex items-center gap-1.5 text-xs text-dark-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={specialFormResolutionIds.includes(res.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSpecialFormResolutionIds([...specialFormResolutionIds, res.id]);
                        } else {
                          setSpecialFormResolutionIds(specialFormResolutionIds.filter(id => id !== res.id));
                        }
                      }}
                      className="rounded border-dark-500"
                    />
                    {res.name}
                  </label>
                ))}
              </div>
              <p className="text-xs text-dark-500 mt-1">{t('specialChannels.resolutionsHelp')}</p>
            </div>
            {/* User Agent override */}
            <div>
              <label className="label text-xs">{t('specialChannels.userAgent')}</label>
              <input
                type="text"
                value={specialFormUserAgent}
                onChange={(e) => setSpecialFormUserAgent(e.target.value)}
                className="input text-sm"
              />
              <p className="text-xs text-dark-500 mt-1">{t('specialChannels.userAgentHelp')}</p>
            </div>
            {/* Video Map Override per resolution */}
            {(() => {
              const activeResolutions = specialFormResolutionIds.length > 0
                ? resolutions.filter(r => specialFormResolutionIds.includes(r.id))
                : resolutions;
              return activeResolutions.length > 0 ? (
                <div>
                  <label className="label text-xs">{t('specialChannels.videoMapOverride')}</label>
                  <div className="space-y-1.5 mt-1">
                    {activeResolutions.map((res) => (
                      <div key={res.id} className="flex items-center gap-2">
                        <span className="text-xs text-dark-400 w-16 text-right font-medium">{res.name}</span>
                        <input
                          type="text"
                          value={specialFormVideoMapOverrides[res.name] || ''}
                          onChange={(e) => {
                            setSpecialFormVideoMapOverrides({
                              ...specialFormVideoMapOverrides,
                              [res.name]: e.target.value,
                            });
                          }}
                          placeholder={res.xaccel_value}
                          className="input text-sm flex-1 font-mono"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-dark-500 mt-1">{t('specialChannels.videoMapOverrideHelp')}</p>
                </div>
              ) : null;
            })()}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetSpecialForm}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleAddOrUpdateSpecialRule}
                disabled={!specialFormName.trim() || !specialFormPatterns.trim() || !specialFormChannelName.trim() || isSavingSpecial}
                className="btn-primary text-xs py-1.5 px-3"
              >
                {isSavingSpecial ? <Loader2 size={14} className="animate-spin" /> : null}
                {editingSpecialIdx !== null ? t('common.update') : t('common.create')}
              </button>
            </div>
          </div>
        )}

        {/* Rules List */}
        {specialRules.length === 0 ? (
          <div className="text-center py-6 text-dark-400">
            <Zap size={36} className="mx-auto mb-2 opacity-50" />
            <p>{t('specialChannels.noRules')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {specialRules.map((rule, idx) => {
              const displayName = rule.channel_name || rule.channel_suffix || '';
              const ruleResNames = rule.resolution_ids && rule.resolution_ids.length > 0
                ? resolutions.filter(r => rule.resolution_ids!.includes(r.id)).map(r => r.name)
                : [];
              return (
                <div key={idx} className="flex items-center gap-2 p-3 border border-dark-700 rounded-lg bg-dark-800">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-dark-200">{rule.name}</span>
                      <span className="badge-primary text-xs">{rule.match_type}</span>
                      <span className="text-xs text-dark-500">{rule.num_channels} ch</span>
                      {ruleResNames.length > 0 && (
                        <span className="text-xs text-primary-500">{ruleResNames.join(', ')}</span>
                      )}
                      {rule.xaccel_user_agent && (
                        <span className="text-xs text-warning-600" title={rule.xaccel_user_agent}>UA</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-mono text-dark-400">{displayName}</span>
                      <span className="text-xs text-dark-500">— {rule.match_patterns.join(', ')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => openEditSpecialRule(idx)}
                      className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteSpecialRule(idx)}
                      className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Xaccel Channels Section */}
        <div className="border-t border-dark-700 pt-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-dark-300 flex items-center gap-2">
              <Server size={16} />
              {t('specialChannels.xaccelSection')} ({specialChannels.length})
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleProvisionSpecialChannels}
                disabled={isProvisioningSpecial || specialRules.length === 0}
                className="btn-primary text-xs py-1.5 px-3"
              >
                {isProvisioningSpecial ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {t('specialChannels.createChannels')}
              </button>
              {specialChannels.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!editingCompetition) return;
                      setIsPushingConfig(true);
                      try {
                        if (!(await saveFormIfNeeded())) return;
                        const pushed = await channelsService.pushConfig(editingCompetition.id);
                        if (pushed.failed) toast.error(t('competitions.configPushPartial', { updated: pushed.updated, failed: pushed.failed, names: (pushed.failures || []).map((f) => `${f.channelName}: ${f.error}`).join('; ') }), { duration: 10000 });
                        else toast.success(t('competitions.configUpdated'));
                      } catch (error) {
                        toast.error(getErrorMessage(error) || t('competitions.configUpdateError'));
                      } finally {
                        setIsPushingConfig(false);
                      }
                    }}
                    disabled={isPushingConfig}
                    className="btn-secondary text-xs py-1.5 px-3"
                  >
                    {isPushingConfig ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {t('specialChannels.updateConfig')}
                  </button>
                  {deleteSpecialConfirm ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={handleDeleteSpecialChannels}
                        disabled={isDeletingSpecial}
                        className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                      >
                        {isDeletingSpecial ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteSpecialConfirm(false)}
                        className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteSpecialConfirm(true)}
                      className="btn-secondary text-xs py-1.5 px-3 text-error-600 hover:text-error-700 hover:border-error-200"
                    >
                      <Trash2 size={14} />
                      {t('specialChannels.deleteChannels')}
                    </button>
                  )}
                  {deleteSpecialDbConfirm ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={handleDeleteSpecialChannelsDb}
                        disabled={isDeletingSpecialDb}
                        className="p-1.5 rounded-lg bg-orange-600 text-white hover:bg-orange-500"
                      >
                        {isDeletingSpecialDb ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteSpecialDbConfirm(false)}
                        className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteSpecialDbConfirm(true)}
                      className="btn-secondary text-xs py-1.5 px-3 text-orange-600 hover:text-orange-700 hover:border-orange-200"
                      title={t('competitions.dbOnlyDeleteHint')}
                    >
                      <Trash2 size={14} />
                      {t('specialChannels.deleteChannelsDb')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {isLoadingSpecialChannels ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={20} className="animate-spin text-primary-600" />
              <span className="ml-2 text-dark-400 text-sm">{t('common.loading')}</span>
            </div>
          ) : specialChannels.length === 0 ? (
            <div className="text-center py-6 text-dark-400">
              <Tv size={36} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('specialChannels.noChannels')}</p>
              <p className="text-xs mt-1">{t('specialChannels.noChannelsHelp')}</p>
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-700 text-dark-400 text-xs">
                    <th className="text-left py-2 px-2">{t('linear.channelName')}</th>
                    <th className="text-left py-2 px-2">{t('linear.resolution')}</th>
                    <th className="text-left py-2 px-2">{t('common.server')}</th>
                    <th className="text-left py-2 px-2">{t('specialChannels.name')}</th>
                    <th className="text-right py-2 px-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {specialChannels.map((ch) => {
                    const srv = xaccelServers.find(s => s.id === ch.xaccel_server_id);
                    return (
                    <tr key={ch.id} className="border-b border-dark-800 group">
                      <td className="py-1.5 px-2 font-mono text-dark-200 text-xs">{ch.channel_name}</td>
                      <td className="py-1.5 px-2">
                        <span className="badge-primary text-xs">{ch.resolution_name}</span>
                      </td>
                      <td className="py-1.5 px-2">
                        {srv ? (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${srv.server_type === 'ffstream' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {srv.name}
                          </span>
                        ) : (
                          <span className="text-dark-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-dark-400 text-xs">{ch.special_rule_name || '-'}</td>
                      <td className="py-1.5 px-2 text-right whitespace-nowrap">
                        {specialChannelDeleteConfirmId === ch.id ? (
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              type="button"
                              onClick={() => handleDeleteSpecialChannel(ch.id)}
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-error-600 text-white hover:bg-error-500"
                              title={t('common.delete')}
                            >
                              {t('common.delete')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteSpecialChannelDb(ch.id)}
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-500 text-white hover:bg-orange-400"
                              title={t('competitions.dbOnlyDeleteHint')}
                            >
                              DB
                            </button>
                            <button
                              type="button"
                              onClick={() => setSpecialChannelDeleteConfirmId(null)}
                              className="p-0.5 rounded hover:bg-dark-600 text-dark-400"
                            >
                              <X size={10} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setSpecialChannelDeleteConfirmId(ch.id)}
                            className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1 rounded hover:bg-error-50 text-dark-400 hover:text-error-600 transition-opacity"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================================================
  // Channels Tab
  // ============================================================================

  const [channels, setChannels] = useState<CompetitionChannel[]>([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  // Busy while the create / delete preview of the regular channels is open (the modal runs the action).
  const isProvisioning = compChannelsReq?.action === 'create' && compChannelsReq.scope === 'all';
  const isDeletingChannels = compChannelsReq?.action === 'delete' && compChannelsReq.scope === 'regular';
  const [isPushingConfig, setIsPushingConfig] = useState(false);
  const [deleteChannelsConfirm, setDeleteChannelsConfirm] = useState(false);
  const [deleteChannelsDbConfirm, setDeleteChannelsDbConfirm] = useState(false);
  const [isDeletingChannelsDb, setIsDeletingChannelsDb] = useState(false);
  const [isSyncingChannels, setIsSyncingChannels] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [channelDeleteConfirmId, setChannelDeleteConfirmId] = useState<number | null>(null);
  const [deleteDropdownOpen, setDeleteDropdownOpen] = useState<'server' | 'db' | null>(null);
  const deleteDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!deleteDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (deleteDropdownRef.current && !deleteDropdownRef.current.contains(e.target as Node)) {
        setDeleteDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [deleteDropdownOpen]);

  // Compute unique server IDs from channels (for per-server delete dropdown)
  const deleteServerIds = [...new Set(channels.map(ch => ch.xaccel_server_id))];
  const hasMultipleServers = deleteServerIds.length > 1;

  const [specialChannelDeleteConfirmId, setSpecialChannelDeleteConfirmId] = useState<number | null>(null);

  const fetchChannelsForCompetition = async (competitionId: number) => {
    setIsLoadingChannels(true);
    try {
      const data = await channelsService.list(competitionId);
      setChannels(data);
    } catch (error) {
      console.error('Failed to fetch channels:', error);
      setChannels([]);
    } finally {
      setIsLoadingChannels(false);
    }
  };

  const handleProvisionChannels = async () => {
    if (!editingCompetition) return;
    // Auto-save form before the preview so the plan uses the latest values
    const saved = await saveFormIfNeeded();
    if (!saved) return;
    setCompChannelsReq({ action: 'create', scope: 'all', serverIds: watch('server_ids') || [] });
  };

  /** Preview of deleting the regular (numbered / team) channels, of one server or of all their servers. */
  const handleDeleteAllChannels = async (serverId?: number) => {
    if (!editingCompetition) return;
    const saved = await saveFormIfNeeded();
    if (!saved) return;
    setDeleteChannelsConfirm(false);
    const rows = channels.filter((c) => c.channel_type !== 'special' && (serverId === undefined || c.xaccel_server_id === serverId));
    setCompChannelsReq({ action: 'delete', scope: 'regular', serverIds: [...new Set(rows.map((c) => c.xaccel_server_id))], legIds: rows.map((c) => c.id) });
  };

  const handleDeleteAllChannelsDb = async () => {
    if (!editingCompetition) return;
    setIsDeletingChannelsDb(true);
    try {
      await channelsService.deleteAllDbOnly(editingCompetition.id);
      toast.success(t('common.deleted'));
      setChannels([]);
      setDeleteChannelsDbConfirm(false);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsDeletingChannelsDb(false);
    }
  };

  const handleDeleteChannel = async (channelId: number) => {
    if (!editingCompetition) return;
    try {
      await channelsService.deleteSingle(editingCompetition.id, channelId);
      setChannels(prev => prev.filter(c => c.id !== channelId));
      setChannelDeleteConfirmId(null);
      toast.success(t('common.deleted'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDeleteChannelDb = async (channelId: number) => {
    if (!editingCompetition) return;
    try {
      await channelsService.deleteSingleDbOnly(editingCompetition.id, channelId);
      setChannels(prev => prev.filter(c => c.id !== channelId));
      setChannelDeleteConfirmId(null);
      toast.success(t('common.deleted'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleRegenerateSchedule = async () => {
    if (!editingCompetition) return;

    setIsRegenerating(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const result = await scheduleService.generateFromEpg(today, [editingCompetition.id]);
      toast.success(t('competitions.scheduleRegenerated', { count: result.data?.length || 0 }));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsRegenerating(false);
    }
  };

  const renderChannelsTab = () => {
    const gateServerIds: number[] = watch('server_ids') || [];
    if (!editingCompetition?.xaccel_server_id && gateServerIds.length === 0) {
      return (
        <div className="text-center py-8 text-dark-400">
          <Server size={48} className="mx-auto mb-3 opacity-50" />
          <p>{t('competitions.streaming')}</p>
          <p className="text-sm mt-1">
            {t('common.configure')}
          </p>
        </div>
      );
    }

    if (isLoadingChannels) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin text-primary-600" />
          <span className="ml-2 text-dark-400 text-sm">{t('common.loading')}</span>
        </div>
      );
    }

    const nonSpecialChannels = channels.filter(c => c.channel_type !== 'special');
    const numberedChannels = channels.filter(c => c.channel_type === 'numbered');
    const teamChannels = channels.filter(c => c.channel_type === 'team');
    const uniqueResolutions = [...new Set(nonSpecialChannels.map(c => c.resolution_name))];

    // Check which servers still need channels OR which resolutions are missing on existing servers
    const formServerIds: number[] = watch('server_ids') || [];
    const formResolutionIds: number[] = watch('resolution_ids') || [];
    const formResolutionNames = resolutions.filter(r => formResolutionIds.includes(r.id)).map(r => r.name);
    const channelServerIds = new Set(nonSpecialChannels.map(c => c.xaccel_server_id));
    const missingServerIds = formServerIds.filter(sid => !channelServerIds.has(sid));
    const missingServers = missingServerIds.map(sid => xaccelServers.find(s => s.id === sid)).filter(Boolean);

    // Check if any existing server is missing resolutions
    const hasMissingResolutions = formServerIds.some(sid => {
      if (!channelServerIds.has(sid)) return false; // fully missing server handled above
      const existingRes = new Set(
        nonSpecialChannels
          .filter(ch => ch.xaccel_server_id === sid)
          .map(ch => ch.resolution_name)
      );
      return formResolutionNames.some(rn => !existingRes.has(rn));
    });
    const needsProvisioning = missingServers.length > 0 || hasMissingResolutions;

    // Build dynamic button label based on target servers
    const buildCreateLabel = (servers: typeof missingServers) => {
      if (servers.length === 0) {
        const allServers = formServerIds.map(sid => xaccelServers.find(s => s.id === sid)).filter(Boolean);
        if (allServers.length === 0) return t('competitions.createChannels');
        return t('competitions.createChannelsOn', { servers: allServers.map(s => s!.name).join(' + ') });
      }
      return t('competitions.createChannelsOn', { servers: servers.map(s => s!.name).join(' + ') });
    };

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-dark-300 flex items-center gap-2">
            <Tv size={16} />
            {t('competitions.channels')} ({nonSpecialChannels.length})
          </h3>
          {nonSpecialChannels.length === 0 ? (
            <button
              type="button"
              onClick={handleProvisionChannels}
              disabled={isProvisioning}
              className="btn-primary text-xs py-1.5 px-3"
            >
              {isProvisioning ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Plus size={14} />
              )}
              {buildCreateLabel([])}
            </button>
          ) : (<>
            <div className="flex items-center gap-2">
              {/* Create on missing servers or missing resolutions */}
              {needsProvisioning && (
                <button
                  type="button"
                  onClick={handleProvisionChannels}
                  disabled={isProvisioning}
                  className="btn-primary text-xs py-1.5 px-3"
                >
                  {isProvisioning ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Plus size={14} />
                  )}
                  {buildCreateLabel(missingServers)}
                </button>
              )}
              <button
                type="button"
                onClick={async () => {
                  if (!editingCompetition) return;
                  setIsPushingConfig(true);
                  try {
                    // Save form first (without closing modal)
                    const data = getValues();
                    const vmOverrides = data.video_map_overrides || {};
                    const hasOverrides = Object.values(vmOverrides).some(v => v && v.trim() !== '');
                    await competitionsService.update(editingCompetition.id, {
                      sport_id: data.sport_id,
                      name: data.name,
                      dazn_asset_id: data.dazn_asset_id || undefined,
                      xaccel_server_id: data.server_ids?.length > 0 ? data.server_ids[0] : (data.xaccel_server_id || undefined),
                      server_ids: data.server_ids?.length > 0 ? data.server_ids : undefined,
                      channel_prefix: data.channel_prefix || undefined,
                      cdn_blacklist: JSON.stringify(data.cdn_blacklist || []),
                      cdn_preferred: JSON.stringify(data.cdn_preferred || []),
                      auto_stop_minutes: data.auto_stop_minutes,
                      stop_margin_minutes: data.stop_margin_minutes,
                      num_channels: data.num_channels,
                      resolution_ids: data.resolution_ids,
                      channel_mode: data.channel_mode,
                      is_active: data.is_active,
                      video_map_overrides: hasOverrides ? JSON.stringify(vmOverrides) : null,
                    });
                    // Push config to Xaccel
                    const pushed = await channelsService.pushConfig(editingCompetition.id);
                    if (pushed.failed) toast.error(t('competitions.configPushPartial', { updated: pushed.updated, failed: pushed.failed, names: (pushed.failures || []).map((f) => `${f.channelName}: ${f.error}`).join('; ') }), { duration: 10000 });
                    else toast.success(t('competitions.configUpdated'));
                    fetchData();
                  } catch (error) {
                    toast.error(getErrorMessage(error) || t('competitions.configUpdateError'));
                  } finally {
                    setIsPushingConfig(false);
                  }
                }}
                disabled={isPushingConfig}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                {isPushingConfig ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {t('competitions.updateConfig')}
              </button>
              {/* Delete from Server — with per-server dropdown if multi-server */}
              <div className="relative" ref={hasMultipleServers ? deleteDropdownRef : undefined}>
                <div className="flex items-center">
                  {deleteChannelsConfirm ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleDeleteAllChannels()}
                        disabled={isDeletingChannels}
                        className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                      >
                        {isDeletingChannels ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteChannelsConfirm(false)}
                        className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteChannelsConfirm(true)}
                      className={`btn-secondary text-xs py-1.5 px-3 text-error-600 hover:text-error-700 hover:border-error-200 ${hasMultipleServers ? 'rounded-r-none border-r-0' : ''}`}
                    >
                      <Trash2 size={14} />
                      {t('competitions.deleteAllChannels')}
                    </button>
                  )}
                  {hasMultipleServers && !deleteChannelsConfirm && (
                    <button
                      type="button"
                      onClick={() => setDeleteDropdownOpen(deleteDropdownOpen === 'server' ? null : 'server')}
                      className="btn-secondary text-xs py-1.5 px-1.5 text-error-600 hover:text-error-700 hover:border-error-200 rounded-l-none"
                    >
                      <ChevronDown size={12} />
                    </button>
                  )}
                </div>
                {deleteDropdownOpen === 'server' && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-dark-200 py-1 z-50 min-w-[180px]">
                    {deleteServerIds.map(sid => {
                      const srv = xaccelServers.find(s => s.id === sid);
                      if (!srv) return null;
                      const count = channels.filter(ch => ch.xaccel_server_id === sid).length;
                      return (
                        <button
                          key={sid}
                          type="button"
                          onClick={() => {
                            setDeleteDropdownOpen(null);
                            handleDeleteAllChannels(sid);
                          }}
                          disabled={isDeletingChannels}
                          className="w-full text-left px-3 py-2 text-[11px] hover:bg-error-50 text-dark-100 flex items-center gap-2"
                        >
                          <span className={`inline-block w-2 h-2 rounded-full ${srv.server_type === 'ffstream' ? 'bg-purple-500' : 'bg-blue-500'}`} />
                          {srv.name}
                          <span className="text-dark-300 ml-auto">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Delete from DB — with per-server dropdown if multi-server */}
              <div className="relative" ref={hasMultipleServers && deleteDropdownOpen === 'db' ? deleteDropdownRef : undefined}>
                <div className="flex items-center">
                  {deleteChannelsDbConfirm ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={handleDeleteAllChannelsDb}
                        disabled={isDeletingChannelsDb}
                        className="p-1.5 rounded-lg bg-orange-600 text-white hover:bg-orange-500"
                      >
                        {isDeletingChannelsDb ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteChannelsDbConfirm(false)}
                        className="p-1.5 rounded-lg hover:bg-dark-600 text-dark-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteChannelsDbConfirm(true)}
                      className={`btn-secondary text-xs py-1.5 px-3 text-orange-600 hover:text-orange-700 hover:border-orange-200 ${hasMultipleServers ? 'rounded-r-none border-r-0' : ''}`}
                      title={t('competitions.dbOnlyDeleteHint')}
                    >
                      <Trash2 size={14} />
                      {t('competitions.deleteAllChannelsDb')}
                    </button>
                  )}
                  {hasMultipleServers && !deleteChannelsDbConfirm && (
                    <button
                      type="button"
                      onClick={() => setDeleteDropdownOpen(deleteDropdownOpen === 'db' ? null : 'db')}
                      className="btn-secondary text-xs py-1.5 px-1.5 text-orange-600 hover:text-orange-700 hover:border-orange-200 rounded-l-none"
                    >
                      <ChevronDown size={12} />
                    </button>
                  )}
                </div>
                {deleteDropdownOpen === 'db' && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-dark-200 py-1 z-50 min-w-[180px]">
                    {deleteServerIds.map(sid => {
                      const srv = xaccelServers.find(s => s.id === sid);
                      if (!srv) return null;
                      const count = channels.filter(ch => ch.xaccel_server_id === sid).length;
                      return (
                        <button
                          key={sid}
                          type="button"
                          onClick={async () => {
                            if (!editingCompetition) return;
                            setDeleteDropdownOpen(null);
                            setIsDeletingChannelsDb(true);
                            try {
                              await channelsService.deleteAllDbOnly(editingCompetition.id, sid);
                              toast.success(t('common.deleted'));
                              await fetchChannelsForCompetition(editingCompetition.id);
                            } catch (error) {
                              toast.error(getErrorMessage(error) || t('common.error'));
                            } finally {
                              setIsDeletingChannelsDb(false);
                            }
                          }}
                          disabled={isDeletingChannelsDb}
                          className="w-full text-left px-3 py-2 text-[11px] hover:bg-orange-50 text-dark-100 flex items-center gap-2"
                        >
                          <span className={`inline-block w-2 h-2 rounded-full ${srv.server_type === 'ffstream' ? 'bg-purple-500' : 'bg-blue-500'}`} />
                          {srv.name}
                          <span className="text-dark-300 ml-auto">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={async () => {
                  if (!editingCompetition) return;
                  setIsSyncingChannels(true);
                  try {
                    const result = await channelsService.syncFromServer(editingCompetition.id);
                    if (result.created > 0) {
                      toast.success(t('competitions.channelsSynced', { created: result.created }));
                      await fetchChannelsForCompetition(editingCompetition.id);
                    } else {
                      toast.success(t('competitions.channelsSyncNoNew'));
                    }
                  } catch (error) {
                    toast.error(compApiErrorText({ code: getErrorCode(error), message: getErrorMessage(error) }, t) || t('common.error'));
                  } finally {
                    setIsSyncingChannels(false);
                  }
                }}
                disabled={isSyncingChannels}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                {isSyncingChannels ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {t('competitions.syncChannels')}
              </button>
            </div>
          </>)}
        </div>

        {/* Schedule Actions */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-dark-300">{t('competitions.scheduleActions')}</h4>
              <p className="text-xs text-dark-400 mt-0.5">{t('competitions.regenerateScheduleHint')}</p>
            </div>
            <button
              type="button"
              onClick={handleRegenerateSchedule}
              disabled={isRegenerating}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              {isRegenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {t('competitions.regenerateSchedule')}
            </button>
          </div>
        </div>

        {/* Stats */}
        {nonSpecialChannels.length > 0 && (
          <div className="flex gap-4 text-xs text-dark-400">
            <span>{numberedChannels.length} {t('schedule.channelModeNumbered').toLowerCase()}</span>
            <span>{teamChannels.length} team</span>
            <span>{uniqueResolutions.length} {t('competitions.resolutions').toLowerCase()} ({uniqueResolutions.join(', ')})</span>
          </div>
        )}

        {/* Channel List */}
        {nonSpecialChannels.length === 0 ? (
          <div className="text-center py-8 text-dark-400">
            <Tv size={48} className="mx-auto mb-3 opacity-50" />
            <p>{t('common.noData')}</p>
            <p className="text-sm mt-1">
              {t('competitions.createChannelsHelp')}
            </p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700 text-dark-400 text-xs">
                  <th className="text-left py-2 px-2">{t('linear.channelName')}</th>
                  <th className="text-left py-2 px-2">{t('linear.resolution')}</th>
                  <th className="text-left py-2 px-2">{t('common.server')}</th>
                  <th className="text-left py-2 px-2">{t('filters.type')}</th>
                  <th className="text-left py-2 px-2">Team</th>
                  <th className="text-right py-2 px-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {nonSpecialChannels.map((ch) => {
                  const srv = xaccelServers.find(s => s.id === ch.xaccel_server_id);
                  return (
                  <tr key={ch.id} className="border-b border-dark-800 group">
                    <td className="py-1.5 px-2 font-mono text-dark-200 text-xs">{ch.channel_name}</td>
                    <td className="py-1.5 px-2">
                      <span className="badge-primary text-xs">{ch.resolution_name}</span>
                    </td>
                    <td className="py-1.5 px-2">
                      {srv ? (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${srv.server_type === 'ffstream' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {srv.name}
                        </span>
                      ) : (
                        <span className="text-dark-500 text-xs">-</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2">
                      <span className={`text-xs ${ch.channel_type === 'numbered' ? 'text-dark-400' : 'text-primary-600'}`}>
                        {ch.channel_type === 'numbered' ? t('schedule.channelModeNumbered') : 'Team'}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 text-dark-400 text-xs">{ch.team_name || '-'}</td>
                    <td className="py-1.5 px-2 text-right whitespace-nowrap">
                      {channelDeleteConfirmId === ch.id ? (
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            onClick={() => handleDeleteChannel(ch.id)}
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-error-600 text-white hover:bg-error-500"
                            title={t('common.delete')}
                          >
                            {t('common.delete')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteChannelDb(ch.id)}
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-500 text-white hover:bg-orange-400"
                            title={t('competitions.dbOnlyDeleteHint')}
                          >
                            DB
                          </button>
                          <button
                            type="button"
                            onClick={() => setChannelDeleteConfirmId(null)}
                            className="p-0.5 rounded hover:bg-dark-600 text-dark-400"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setChannelDeleteConfirmId(ch.id)}
                          className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1 rounded hover:bg-error-50 text-dark-400 hover:text-error-600 transition-opacity"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('competitions.title')}</h1>
          <p className="text-dark-400 mt-1">{competitions.length} {t('competitions.title').toLowerCase()}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {competitions.length > 0 && (
            deleteAllConfirm ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={handleDeleteAll}
                  className="p-2 rounded-lg bg-error-600 text-white hover:bg-error-500"
                  title={t('common.confirmDelete')}
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => setDeleteAllConfirm(false)}
                  className="p-2 rounded-lg hover:bg-dark-700 text-dark-400"
                  title={t('common.cancel')}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setDeleteAllConfirm(true)}
                className="btn-secondary text-error-600 hover:bg-error-50"
              >
                <Trash2 size={16} />
                {t('common.deleteAll')}
              </button>
            )
          )}
          <button onClick={openCreateModal} className="btn-primary" disabled={sports.length === 0}>
            <Plus size={18} />
            {t('competitions.add')}
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter size={18} className="text-dark-400" />
        <select
          value={filterSportId || ''}
          onChange={(e) => setFilterSportId(e.target.value ? Number(e.target.value) : undefined)}
          className="input w-auto"
        >
          <option value="">{t('competitions.allSports')}</option>
          {sports.map((sport) => (
            <option key={sport.id} value={sport.id}>{sport.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : competitions.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('common.noData')}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t('competitions.name')}</th>
                <th>{t('sports.title')}</th>
                <th>{t('common.server')}</th>
                <th>{t('competitions.channelPrefix')}</th>
                <th>{t('competitions.resolutions')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {competitions.map((comp) => (
                <tr key={comp.id}>
                  <td>
                    <div>
                      <p className="font-medium text-dark-100">{comp.name}</p>
                      {comp.dazn_asset_id && (
                        <p className="text-xs text-dark-500 font-mono truncate max-w-[150px]">
                          {comp.dazn_asset_id}
                        </p>
                      )}
                    </div>
                  </td>
                  <td>{comp.sport_name || '-'}</td>
                  <td>
                    {(() => {
                      const sids = parseServerIds(comp.server_ids);
                      if (sids.length > 0) {
                        return (
                          <div className="flex flex-wrap gap-1">
                            {sids.map(sid => {
                              const srv = xaccelServers.find(s => s.id === sid);
                              if (!srv) return null;
                              return (
                                <span
                                  key={sid}
                                  className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
                                    srv.server_type === 'ffstream'
                                      ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                      : 'bg-blue-50 text-blue-700 border border-blue-200'
                                  }`}
                                >
                                  {srv.name}
                                </span>
                              );
                            })}
                          </div>
                        );
                      }
                      return <span className="text-dark-500 text-sm">{comp.xaccel_server_name || '-'}</span>;
                    })()}
                  </td>
                  <td className="text-dark-400 font-mono text-sm">{comp.channel_prefix || '-'}</td>
                  <td>{renderResolutionBadges(comp)}</td>
                  <td>{renderStatusBadge(comp)}</td>
                  <td>
                    {deleteConfirm === comp.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(comp.id)}
                          className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(comp)}
                          className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(comp.id)}
                          className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingCompetition && (
        <CompetitionChannelsModal
          competitionId={editingCompetition.id}
          request={compChannelsReq}
          onClose={() => setCompChannelsReq(null)}
          onDone={() => { fetchChannelsForCompetition(editingCompetition.id); fetchSpecialChannels(editingCompetition.id); }}
        />
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div className="card w-full max-w-6xl animate-fade-in flex flex-col max-h-[calc(100vh-2rem)] !p-0" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingCompetition ? editingCompetition.name : t('competitions.add')}
              </h2>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-4 md:px-6 border-b border-dark-700 flex-shrink-0 overflow-x-auto scrollbar-hide">
              {([
                { id: 'general', label: t('competitions.general'), icon: Settings },
                { id: 'streaming', label: t('competitions.streaming'), icon: Tv },
                { id: 'teams', label: t('competitions.teams'), icon: Users, disabled: !editingCompetition },
                { id: 'filters', label: t('competitions.filters'), icon: Filter, disabled: !editingCompetition },
                { id: 'channels', label: t('competitions.channels'), icon: Tv, disabled: !editingCompetition },
                { id: 'special', label: t('specialChannels.title'), icon: Zap, disabled: !editingCompetition, hidden: watch('channel_mode') === 'numbered' },
              ] as Array<{ id: string; label: string; icon: any; disabled?: boolean; hidden?: boolean }>).filter(tab => !tab.hidden).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as ModalTab)}
                  disabled={tab.disabled}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors
                    ${activeTab === tab.id
                      ? 'bg-dark-700 text-dark-100 border-b-2 border-primary-500'
                      : 'text-dark-400 hover:text-dark-200 hover:bg-dark-800'
                    }
                    ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col min-h-0 flex-1">
              <div className="flex-1 overflow-y-auto px-6 py-6">
                {activeTab === 'general' && renderGeneralTab()}
                {activeTab === 'streaming' && renderStreamingTab()}
                {activeTab === 'teams' && renderTeamsTab()}
                {activeTab === 'filters' && renderFiltersTab()}
                {activeTab === 'channels' && renderChannelsTab()}
                {activeTab === 'special' && renderSpecialChannelsTab()}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-dark-700 flex-shrink-0">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex items-center gap-1.5"
                >
                  {isSubmitting ? t('common.loading') : t('common.save')}
                  {isDirty && !isSubmitting && (
                    <span className="w-2 h-2 rounded-full bg-amber-400" title={t('common.unsavedChanges')} />
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
